/** Key under which `@tanstack/react-query-persist-client` stores the dehydrated
 * query cache. It's the largest, most disposable entry, so it's what we evict
 * first to make room when the quota is hit. */
export const REACT_QUERY_CACHE_KEY = "REACT_QUERY_OFFLINE_CACHE";

/** Detect a localStorage "out of space" error across browsers. */
const isQuotaExceeded = (e: unknown): boolean =>
  e instanceof DOMException &&
  // Standard name, Firefox legacy name, and the historical numeric codes.
  (e.name === "QuotaExceededError" ||
    e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    e.code === 22 ||
    e.code === 1014);

/**
 * A `localStorage`-backed key/value store whose writes never throw.
 *
 * When the browser quota is exceeded, a normal `setItem` throws
 * `QuotaExceededError`; if that happens inside a React render/effect (e.g. a
 * zustand `persist` write) it crashes the subtree. This wrapper instead evicts
 * the disposable React Query cache and retries once, and if it still can't fit,
 * drops the write with a warning. Reads are likewise guarded.
 *
 * Shaped to satisfy both `createSyncStoragePersister` and zustand's
 * `createJSONStorage` (`getItem`/`setItem`/`removeItem`).
 */
export const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  setItem: (key: string, value: string): void => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      if (!isQuotaExceeded(e)) {
        console.warn(`[safeStorage] write failed for "${key}"`, e);
        return;
      }
      // Free the biggest disposable entry and retry once — unless the write that
      // overflowed IS that entry, in which case just skip persisting it.
      if (key !== REACT_QUERY_CACHE_KEY) {
        try {
          window.localStorage.removeItem(REACT_QUERY_CACHE_KEY);
          window.localStorage.setItem(key, value);
          return;
        } catch {
          // Still no room — fall through to dropping the write.
        }
      }
      console.warn(`[safeStorage] dropped write for "${key}" — storage quota exceeded`, e);
    }
  },

  removeItem: (key: string): void => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
